import { useEffect, useRef, useState } from "react";

// Аудио-плеер: сверху прогресс-бар + таймкоды, снизу ряд кнопок −5 / Play / +5.
// HTML5 <audio> без controls — рисуем свой UI: Play/Pause, скраббер, тайминги, два seek-jump.
// Скраббер — нативный <input type="range">, чтобы тач-драг работал из коробки на iOS.

type Props = {
  src: string;
  title?: string | null;
  // Длинная подпись под плеером — мелким приглушённым шрифтом, ниже кнопок.
  caption?: string | null;
};

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function AudioPlayer({ src, title, caption }: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // Юзер тянет скраббер пальцем — на время drag не даём audio.timeupdate перезаписывать
  // отображаемое значение, иначе бегунок дёргается.
  const [seeking, setSeeking] = useState(false);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => {
      if (!seeking) setCurrentTime(a.currentTime);
    };
    const onMeta = () => setDuration(a.duration || 0);
    const onEnded = () => setPlaying(false);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("durationchange", onMeta);
    a.addEventListener("ended", onEnded);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("durationchange", onMeta);
      a.removeEventListener("ended", onEnded);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
    };
  }, [seeking]);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => {});
    else a.pause();
  };

  const jump = (delta: number) => {
    const a = audioRef.current;
    if (!a) return;
    const next = Math.max(0, Math.min(duration || a.duration || 0, a.currentTime + delta));
    a.currentTime = next;
    setCurrentTime(next);
  };

  const onScrubChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    setCurrentTime(v);
  };

  const onScrubCommit = (e: React.ChangeEvent<HTMLInputElement> | React.PointerEvent<HTMLInputElement>) => {
    const a = audioRef.current;
    if (!a) return;
    const target = e.target as HTMLInputElement;
    const v = parseFloat(target.value);
    a.currentTime = v;
    setSeeking(false);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="audio-player">
      {title && <div className="audio-player-title">{title}</div>}
      <audio ref={audioRef} src={src} preload="metadata" />

      <div className="audio-player-scrub">
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          onChange={onScrubChange}
          onPointerDown={() => setSeeking(true)}
          onPointerUp={onScrubCommit}
          onPointerCancel={onScrubCommit}
          aria-label="Прогресс"
          style={{
            // SSR-safe: inline-стиль фоновой заливки от 0 до progress%.
            background: `linear-gradient(to right,
              var(--tg-theme-button-color, #2481cc) 0%,
              var(--tg-theme-button-color, #2481cc) ${progress}%,
              var(--card-surface-strong) ${progress}%,
              var(--card-surface-strong) 100%)`,
          }}
        />
      </div>

      <div className="audio-player-times">
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>

      <div className="audio-player-row">
        <button
          type="button"
          className="audio-player-jump"
          onClick={() => jump(-5)}
          aria-label="Назад 5 секунд"
        >
          <span className="audio-player-jump-num">−5</span>
        </button>

        <button
          type="button"
          className="audio-player-play"
          onClick={togglePlay}
          aria-label={playing ? "Пауза" : "Воспроизвести"}
        >
          {playing ? (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M7 5.5v13a1 1 0 0 0 1.55.832l10-6.5a1 1 0 0 0 0-1.664l-10-6.5A1 1 0 0 0 7 5.5z" />
            </svg>
          )}
        </button>

        <button
          type="button"
          className="audio-player-jump"
          onClick={() => jump(5)}
          aria-label="Вперёд 5 секунд"
        >
          <span className="audio-player-jump-num">+5</span>
        </button>
      </div>

      {caption && <p className="media-caption">{caption}</p>}
    </div>
  );
}
